// IHOG-rings GeoJSON builder.
//
// Reads the in-memory incidents list and the resolved per-incident
// radii, and produces a FeatureCollection MapLibre can render. Two
// features per active incident with valid lat/lon — one for the
// contact ring (outer) and one for the exclusion ring (inner).
//
// Pure module — no map / no React. Drives a single source that
// MapView re-feeds whenever the incidents list changes.

import type { Feature, FeatureCollection, Polygon } from 'geojson';
import { geodesicCircleRing } from '../lib/geo';
import {
  IHOG_DEFAULT_CONTACT_NM,
  IHOG_DEFAULT_EXCLUSION_NM,
  resolveRadiiNm,
} from '../lib/ihogProximity';
import type { Incident } from '../stores/incidentStore';

/** Properties carried on every IHOG-ring feature for the renderer. */
export interface IhogRingProps {
  incidentId: string;
  incidentName: string;
  /** 'contact' or 'exclusion' — controls fill / outline style. */
  kind: 'contact' | 'exclusion';
  radiusNm: number;
}

/**
 * Build the FeatureCollection for the active-incidents IHOG-rings
 * layer. Skips incidents without lat/lon and non-'active' status.
 */
export function buildIhogRingsFC(incidents: ReadonlyArray<Incident>): FeatureCollection<Polygon, IhogRingProps> {
  const features: Array<Feature<Polygon, IhogRingProps>> = [];
  for (const inc of incidents) {
    if (inc.status !== 'active') continue;
    if (inc.latitude == null || inc.longitude == null) continue;
    if (!Number.isFinite(inc.latitude) || !Number.isFinite(inc.longitude)) continue;

    const { contactNm, exclusionNm } = resolveRadiiNm(inc);

    // Generate both rings as closed polygons. Higher segment count for
    // exclusion since it's the inner / more visually prominent one.
    const contactCoords   = geodesicCircleRing(inc.latitude, inc.longitude, contactNm,   72);
    const exclusionCoords = geodesicCircleRing(inc.latitude, inc.longitude, exclusionNm, 64);

    features.push({
      type: 'Feature',
      properties: {
        incidentId:   inc.id,
        incidentName: inc.name,
        kind:         'contact',
        radiusNm:     contactNm,
      },
      geometry: { type: 'Polygon', coordinates: [contactCoords] },
    });

    features.push({
      type: 'Feature',
      properties: {
        incidentId:   inc.id,
        incidentName: inc.name,
        kind:         'exclusion',
        radiusNm:     exclusionNm,
      },
      geometry: { type: 'Polygon', coordinates: [exclusionCoords] },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** Empty FeatureCollection sentinel for source initialization. */
export const EMPTY_IHOG_FC: FeatureCollection<Polygon, IhogRingProps> = {
  type: 'FeatureCollection',
  features: [],
};

// Re-export defaults so MapView/UI don't need to import from two places.
export { IHOG_DEFAULT_CONTACT_NM, IHOG_DEFAULT_EXCLUSION_NM };
